import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Stack} from '@mui/material';
import {AutocompleteField, InputField} from '@/pages/components/common';
import {listHsicIndustries, recommendHsicIndustries, type HsicIndustryRecommendVO} from '@/services/manageService/hsicIndustryService';
import {
    hsicOptionLabelToSaved,
    hsicSavedToOptionLabel,
    INACTIVE_HSIC_CODE,
    isInactiveHsicSaved,
    joinBusinessNature,
    splitBusinessNature,
    toHsicOptionLabel,
} from '@/utils/businessNatureHsic';

export type BusinessNatureFieldsProps = {
    /** Combined persisted value (`text || hsic` / `||hsic` / `text||`). */
    value: string | null;
    onChange: (combined: string | null) => void;
    /** EpBusiness: outlined labels on both controls. */
    showFieldLabels?: boolean;
    disabled?: boolean;
    /** Principal activities Inactive/dormant → force HSIC 000000. */
    inactivePrincipal?: boolean;
    onError?: (message: string) => void;
    /** Dialog stacking for Autocomplete popper (SalesIncomeWp). */
    autocompleteSlotProps?: Record<string, unknown>;
};

/**
 * Business Nature: left free-text Input (50%) + right HSIC Autocomplete (50%).
 * AI Top5 when free-text has value; full dictionary on Autocomplete focus when free-text is empty.
 */
export function BusinessNatureFields({
    value,
    onChange,
    showFieldLabels = false,
    disabled = false,
    inactivePrincipal = false,
    onError,
    autocompleteSlotProps,
}: BusinessNatureFieldsProps) {
    const [freeText, setFreeText] = useState('');
    const [hsicOption, setHsicOption] = useState<string | null>(null);
    const localPartsRef = useRef({freeText: '', hsicOption: null as string | null});
    localPartsRef.current = {freeText, hsicOption};
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const [hsicDictLabels, setHsicDictLabels] = useState<string[]>([]);
    const [hsicDictLoading, setHsicDictLoading] = useState(false);
    const hsicDictLoadedRef = useRef(false);

    const [hsicAiMode, setHsicAiMode] = useState(false);
    const [hsicRecommendList, setHsicRecommendList] = useState<HsicIndustryRecommendVO[]>([]);
    const [hsicRecommendLoading, setHsicRecommendLoading] = useState(false);
    const hsicRecommendSeqRef = useRef(0);
    const hsicInputDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** Sync from persisted value only when it differs from the joined local parts (avoids trimming mid-type). */
    useEffect(() => {
        const parts = splitBusinessNature(value);
        const fromValue = joinBusinessNature(parts.freeText, parts.hsicSaved);
        const local = localPartsRef.current;
        const fromLocal = joinBusinessNature(
            local.freeText,
            local.hsicOption ? hsicOptionLabelToSaved(local.hsicOption) : '',
        );
        if (fromValue === fromLocal) return;
        setFreeText(parts.freeText);
        setHsicOption(parts.hsicSaved ? hsicSavedToOptionLabel(parts.hsicSaved) : null);
    }, [value]);

    /** Inactive / dormant principal → HSIC 000000 (keep free-text; default "inactive" if empty). */
    useEffect(() => {
        if (!inactivePrincipal) return;
        const parts = splitBusinessNature(value);
        if (isInactiveHsicSaved(parts.hsicSaved)) return;
        const free = parts.freeText.trim() || 'inactive';
        onChangeRef.current(joinBusinessNature(free, INACTIVE_HSIC_CODE));
    }, [inactivePrincipal, value]);

    const hsicAiOptionLabels = useMemo(
        () => hsicRecommendList.map((item) => toHsicOptionLabel(item.code, item.nameZh)),
        [hsicRecommendList],
    );

    const hsicReasonByOption = useMemo(() => {
        const reasonMap: Record<string, string> = {};
        for (const item of hsicRecommendList) {
            reasonMap[toHsicOptionLabel(item.code, item.nameZh)] = item.reason || '';
        }
        return reasonMap;
    }, [hsicRecommendList]);

    const hsicActiveLabels = hsicAiMode ? hsicAiOptionLabels : hsicDictLabels;

    const businessNatureOptions = useMemo(() => {
        if (hsicOption && !hsicActiveLabels.includes(hsicOption)) {
            return [hsicOption, ...hsicActiveLabels];
        }
        return hsicActiveLabels;
    }, [hsicOption, hsicActiveLabels]);

    const loadHsicDictionary = useCallback(async () => {
        if (hsicDictLoadedRef.current || hsicDictLoading) return;
        setHsicDictLoading(true);
        try {
            const list = await listHsicIndustries({level: 5});
            const labels = (Array.isArray(list) ? list : [])
                .filter((row) => row?.code && row?.nameZh)
                .map((row) => toHsicOptionLabel(row.code, row.nameZh));
            setHsicDictLabels(labels);
            hsicDictLoadedRef.current = true;
        } catch (err) {
            console.warn('HSIC dictionary list failed', err);
            const message = err instanceof Error && err.message
                ? err.message
                : 'HSIC dictionary load failed';
            onError?.(message);
        } finally {
            setHsicDictLoading(false);
        }
    }, [hsicDictLoading, onError]);

    const requestHsicRecommendations = useCallback(async (description: string) => {
        const trimmed = description.trim();
        if (!trimmed) {
            setHsicAiMode(false);
            setHsicRecommendList([]);
            setHsicRecommendLoading(false);
            return;
        }
        const seq = ++hsicRecommendSeqRef.current;
        setHsicAiMode(true);
        setHsicRecommendLoading(true);
        try {
            const list = await recommendHsicIndustries(trimmed);
            if (seq !== hsicRecommendSeqRef.current) return;
            setHsicRecommendList(Array.isArray(list) ? list : []);
        } catch (err) {
            if (seq !== hsicRecommendSeqRef.current) return;
            console.warn('HSIC recommend failed', err);
            setHsicRecommendList([]);
            setHsicAiMode(false);
            const message = err instanceof Error && err.message
                ? err.message
                : 'HSIC recommendation failed';
            onError?.(message);
        } finally {
            if (seq === hsicRecommendSeqRef.current) {
                setHsicRecommendLoading(false);
            }
        }
    }, [onError]);

    const scheduleAiRecommend = useCallback((description: string) => {
        if (hsicInputDebounceRef.current) {
            clearTimeout(hsicInputDebounceRef.current);
        }
        const trimmed = description.trim();
        if (!trimmed) {
            setHsicAiMode(false);
            setHsicRecommendList([]);
            setHsicRecommendLoading(false);
            return;
        }
        hsicInputDebounceRef.current = setTimeout(() => {
            void requestHsicRecommendations(trimmed);
        }, 400);
    }, [requestHsicRecommendations]);

    useEffect(() => () => {
        if (hsicInputDebounceRef.current) {
            clearTimeout(hsicInputDebounceRef.current);
        }
    }, []);

    const handleFreeTextChange = useCallback((next: string) => {
        setFreeText(next);
        setHsicOption(null);
        onChange(joinBusinessNature(next, ''));
        scheduleAiRecommend(next);
    }, [onChange, scheduleAiRecommend]);

    const handleHsicChange = useCallback((optionLabel: string | null) => {
        setHsicOption(optionLabel);
        onChange(joinBusinessNature(freeText, optionLabel ? hsicOptionLabelToSaved(optionLabel) : ''));
    }, [freeText, onChange]);

    const handleAutocompleteFocus = useCallback(() => {
        if (!freeText.trim()) {
            setHsicAiMode(false);
            void loadHsicDictionary();
            return;
        }
        setHsicAiMode(true);
        if (hsicRecommendList.length === 0 && !hsicRecommendLoading) {
            void requestHsicRecommendations(freeText.trim());
        }
    }, [
        freeText,
        hsicRecommendList.length,
        hsicRecommendLoading,
        loadHsicDictionary,
        requestHsicRecommendations,
    ]);

    /** Match AutocompleteField inputRoot (0 root pad + 4px input) so both halves share one height. */
    const halfFieldSx = {
        flex: 1,
        minWidth: 0,
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingTop: 0,
            paddingBottom: 0,
        },
        '& .MuiInputBase-input': {
            paddingTop: '4px !important',
            paddingBottom: '4px !important',
        },
    } as const;

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems={showFieldLabels ? 'flex-start' : 'center'}
            sx={{flex: 1, width: '100%', minWidth: 0}}
        >
            <InputField
                value={freeText}
                onChange={(e) => handleFreeTextChange(e.target.value)}
                display={disabled}
                compact
                align="left"
                fullWidth
                variant={showFieldLabels ? 'outlined' : undefined}
                label={showFieldLabels ? 'Business Nature (業務性質)' : undefined}
                placeholder="Describe business nature"
                sx={halfFieldSx}
            />
            <AutocompleteField
                options={businessNatureOptions}
                value={hsicOption}
                onChange={handleHsicChange}
                onFocus={handleAutocompleteFocus}
                openOnFocus
                getOptionTooltip={(option) => hsicReasonByOption[option] || undefined}
                freeSolo
                commitOnBlur
                loading={hsicDictLoading || hsicRecommendLoading}
                forcePopupIcon
                fullWidthOptions
                disableClientFilter={hsicAiMode}
                align="left"
                disabled={disabled}
                placeholder={freeText.trim()
                    ? 'AI top 5 HSIC suggestions / type code'
                    : 'Focus to browse HSIC dictionary / type code'}
                label={showFieldLabels ? 'HSIC Industry' : undefined}
                fullWidth
                sx={halfFieldSx}
                disablePortal={false}
                slotProps={autocompleteSlotProps}
            />
        </Stack>
    );
}
